import { OfferingDetails, Rejection, Maybe, RemovedRejection } from '@ourbranch/schema-types/appsync';
import { DynamoDbOffer, DynamoDbOverrides } from '@ourbranch/schema-types/dynamodb';
import { softRejectCodes, farmSoftRejectCodes } from '@ourbranch/lookups';

import { ConsumerType, ExecutionType, RejectionLevel, RejectionType, RejectionLevelHelperOptions } from './types';

const softRejectionApplicableTypes = [
  RejectionType.Home,
  RejectionType.MonolineHome,
  RejectionType.Auto,
  RejectionType.Monoline,
  RejectionType.Condo,
  RejectionType.MonolineCondo,
  RejectionType.Farm
];

function getNextRejection(type: RejectionType, rejections: Maybe<Rejection>[]) {
  if (rejections && rejections.length) {
    const rejection = rejections.find((rejection: any) => rejection?.type === type);
    return rejection?.value || null;
  }
  return null;
}

function isSoftRejection(rejection: Rejection) {
  return rejection?.level && rejection?.level === RejectionLevel.Soft;
}

function isHardRejection(rejection: Rejection) {
  return !rejection?.level || rejection?.level === RejectionLevel.Hard;
}

function hasHardRejectCode(rejectCode: number | null = null) {
  return !!rejectCode && !(softRejectCodes as readonly number[]).includes(rejectCode);
}

export function rejectionsUpdater(offer: DynamoDbOffer) {
  if (!offer.rejections) {
    offer.rejections = [];
  }

  if (!offer.removedRejections) {
    offer.removedRejections = [];
  }

  const offeringDetails = offer.quote.offerings as OfferingDetails;
  const rejections = offer.rejections;
  const removedRejections = offer.removedRejections;

  const api = {
    add(code: number, type: RejectionType, level: RejectionLevel = RejectionLevel.Hard, data: any = null) {
      if (!rejections.find((rejection: any) => rejection?.type === type && rejection.value === code)) {
        rejections.push({ value: code, type, level, data });

        switch (type) {
          case RejectionType.Auto:
            offeringDetails.autoRejectCode = code;
            break;
          case RejectionType.Home:
            offeringDetails.homeownersRejectCode = code;
            break;
          case RejectionType.Renters:
            offeringDetails.rentersRejectCode = code;
            break;
          case RejectionType.Condo:
            offeringDetails.condoRejectCode = code;
            break;
          case RejectionType.MonolineHome:
            offeringDetails.monolineHomeRejectCode = code;
            break;
          case RejectionType.MonolineCondo:
            offeringDetails.monolineCondoRejectCode = code;
            break;
          case RejectionType.Farm:
            offeringDetails.farmRejectCode = code;
            break;
          default:
            offeringDetails.monolineAutoRejectCode = code;
            break;
        }
      }
      return api;
    },
    remove(code: Maybe<number>, type: RejectionType) {
      const rejectionIndex = rejections.findIndex(
        (rejection: any) => rejection?.type === type && rejection.value === code
      );
      if (rejectionIndex >= 0) {
        rejections.splice(rejectionIndex, 1);

        const existingRejectionIndex = removedRejections.findIndex((rejection) => rejection?.value === code);
        const date = new Date().toISOString();

        if (existingRejectionIndex >= 0 && removedRejections[existingRejectionIndex]) {
          removedRejections[existingRejectionIndex].date = date;
        } else {
          removedRejections.push({
            offerId: offer.id,
            value: code,
            date
          } as RemovedRejection);
        }
      }

      switch (type) {
        case RejectionType.Auto:
          offeringDetails.autoRejectCode = getNextRejection(type, rejections);
          break;
        case RejectionType.Home:
          offeringDetails.homeownersRejectCode = getNextRejection(type, rejections);
          break;
        case RejectionType.Renters:
          offeringDetails.rentersRejectCode = null;
          break;
        case RejectionType.Condo:
          // Symmetric with Home/Farm: fall back to the next remaining condo rejection rather than
          // nulling unconditionally, so a self-cure (#34385) with another condo rejection still
          // present leaves the correct code on the summary field.
          offeringDetails.condoRejectCode = getNextRejection(type, rejections);
          break;
        case RejectionType.Farm:
          // Symmetric with `add`, which sets `farmRejectCode`. Without this case a removed farm
          // rejection fell to `default:` and both left `farmRejectCode` stale (agents kept seeing a
          // cleared code) and clobbered the unrelated `monolineAutoRejectCode`. (#34260)
          offeringDetails.farmRejectCode = getNextRejection(type, rejections);
          break;
        default:
          offeringDetails.monolineAutoRejectCode = getNextRejection(type, rejections);
          break;
      }

      return api;
    }
  };

  return api;
}

export function rejectionsReader(offer: DynamoDbOffer, softRejectionsEnabled = false) {
  const offeringDetails = offer.quote.offerings as OfferingDetails;
  const rejections = offer.rejections || [];
  const overrides = (offer.quote.overrides as DynamoDbOverrides) || {};

  const api = {
    // Home helpers
    hasHomeRejection() {
      if (softRejectionsEnabled) {
        return (
          rejections.some((rejection: any) => rejection?.type === RejectionType.Home && isHardRejection(rejection)) ||
          hasHardRejectCode(offeringDetails.homeownersRejectCode)
        );
      }

      return (
        rejections.some((rejection: any) => rejection?.type === RejectionType.Home) ||
        !!offeringDetails.homeownersRejectCode
      );
    },
    hasHomeRejectionCode(code: number) {
      return (
        rejections.some((rejection: any) => rejection?.type === RejectionType.Home && rejection.value === code) ||
        offeringDetails.homeownersRejectCode === code
      );
    },
    getHomeRejections() {
      if (softRejectionsEnabled) {
        return rejections.filter(
          (rejection: any) => rejection?.type === RejectionType.Home && isHardRejection(rejection)
        );
      }

      return rejections.filter((rejection: any) => rejection?.type === RejectionType.Home);
    },
    getHomeRejectionsCodes() {
      return this.getErrorCodes(RejectionType.Home);
    },

    // Auto helpers
    hasAutoRejection() {
      if (softRejectionsEnabled) {
        return (
          rejections.some((rejection: any) => rejection?.type === RejectionType.Auto && isHardRejection(rejection)) ||
          hasHardRejectCode(offeringDetails.autoRejectCode)
        );
      }

      return (
        rejections.some((rejection: any) => rejection?.type === RejectionType.Auto) || !!offeringDetails.autoRejectCode
      );
    },
    getAutoRejections() {
      if (softRejectionsEnabled) {
        return rejections.filter(
          (rejection: any) => rejection?.type === RejectionType.Auto && isHardRejection(rejection)
        );
      }

      return rejections.filter((rejection: any) => rejection?.type === RejectionType.Auto);
    },
    getAutoRejectionsCodes() {
      return this.getErrorCodes(RejectionType.Auto);
    },

    // Monoline helpers
    hasMonolineHomeRejection() {
      if (softRejectionsEnabled) {
        return (
          rejections.some(
            (rejection: any) => rejection?.type === RejectionType.MonolineHome && isHardRejection(rejection)
          ) || hasHardRejectCode(offeringDetails.monolineHomeRejectCode)
        );
      }

      return (
        rejections.some((rejection: any) => rejection?.type === RejectionType.MonolineHome) ||
        !!offeringDetails.monolineHomeRejectCode
      );
    },
    hasMonolineCondoRejection() {
      if (softRejectionsEnabled) {
        return (
          rejections.some(
            (rejection: any) => rejection?.type === RejectionType.MonolineCondo && isHardRejection(rejection)
          ) || hasHardRejectCode(offeringDetails.monolineCondoRejectCode)
        );
      }

      return (
        rejections.some((rejection: any) => rejection?.type === RejectionType.MonolineCondo) ||
        !!offeringDetails.monolineCondoRejectCode
      );
    },
    getMonolineHomeRejectionsCodes() {
      return this.getErrorCodes(RejectionType.MonolineHome);
    },
    getMonolineHomeRejections() {
      if (softRejectionsEnabled) {
        return rejections.filter(
          (rejection: any) => rejection?.type === RejectionType.MonolineHome && isHardRejection(rejection)
        );
      }

      return rejections.filter((rejection: any) => rejection?.type === RejectionType.MonolineHome);
    },
    getMonolineCondoRejectionsCodes() {
      return this.getErrorCodes(RejectionType.MonolineCondo);
    },
    getMonolineCondoRejections() {
      if (softRejectionsEnabled) {
        return rejections.filter(
          (rejection: any) => rejection?.type === RejectionType.MonolineCondo && isHardRejection(rejection)
        );
      }

      return rejections.filter((rejection: any) => rejection?.type === RejectionType.MonolineCondo);
    },
    hasMonolineAutoRejection() {
      if (softRejectionsEnabled) {
        return (
          (rejections.some(
            (rejection: any) => rejection?.type === RejectionType.Monoline && isHardRejection(rejection)
          ) ||
            hasHardRejectCode(offeringDetails.monolineAutoRejectCode)) &&
          !overrides.allowMonolineAuto
        );
      }

      return (
        (rejections.some((rejection: any) => rejection?.type === RejectionType.Monoline) ||
          !!offeringDetails.monolineAutoRejectCode) &&
        !overrides.allowMonolineAuto
      );
    },
    getMonolineAutoRejections() {
      if (softRejectionsEnabled) {
        return rejections.filter(
          (rejection: any) => rejection?.type === RejectionType.Monoline && isHardRejection(rejection)
        );
      }

      return rejections.filter((rejection: any) => rejection?.type === RejectionType.Monoline);
    },
    getMonolineAutoRejectionsCodes() {
      return this.getErrorCodes(RejectionType.Monoline);
    },
    hasRentersRejections() {
      if (softRejectionsEnabled) {
        return rejections.some(
          (rejection: any) => rejection?.type === RejectionType.Renters && isHardRejection(rejection)
        );
      }

      return rejections.some((rejection: any) => rejection?.type === RejectionType.Renters);
    },
    getRentersRejections() {
      if (softRejectionsEnabled) {
        return rejections.filter(
          (rejection: any) => rejection?.type === RejectionType.Renters && isHardRejection(rejection)
        );
      }

      return rejections.filter((rejection: any) => rejection?.type === RejectionType.Renters);
    },
    getRentersRejectionsCodes() {
      return this.getErrorCodes(RejectionType.Renters);
    },
    hasCondoRejections() {
      if (softRejectionsEnabled) {
        return rejections.some(
          (rejection: any) => rejection?.type === RejectionType.Condo && isHardRejection(rejection)
        );
      }

      return rejections.some((rejection: any) => rejection?.type === RejectionType.Condo);
    },
    getCondoRejections() {
      if (softRejectionsEnabled) {
        return rejections.filter(
          (rejection: any) => rejection?.type === RejectionType.Condo && isHardRejection(rejection)
        );
      }

      return rejections.filter((rejection: any) => rejection?.type === RejectionType.Condo);
    },
    getCondoRejectionsCodes() {
      return this.getErrorCodes(RejectionType.Condo);
    },

    hasFarmRejections() {
      if (softRejectionsEnabled) {
        return rejections.some((rejection) => rejection?.type === RejectionType.Farm && isHardRejection(rejection));
      }

      return rejections.some((rejection: any) => rejection?.type === RejectionType.Farm);
    },
    getErrorCodes(type: RejectionType) {
      if (softRejectionsEnabled) {
        return rejections
          .filter((rejection: any) => rejection?.type === type && isHardRejection(rejection))
          .map((rejection: any) => rejection?.value);
      }

      return rejections.filter((rejection: any) => rejection?.type === type).map((rejection: any) => rejection?.value);
    },
    hasAnyRejection() {
      if (softRejectionsEnabled) {
        return rejections.filter((rejection: any) => isHardRejection(rejection)).length > 0;
      }

      return rejections.length > 0;
    },
    hasSoftRejection() {
      return rejections.filter((rejection: any) => isSoftRejection(rejection)).length > 0;
    },
    getSoftRejectionsErrorCodes() {
      return rejections
        .filter((rejection: any) => isSoftRejection(rejection))
        .map((rejection: any) => rejection?.value);
    }
  };

  return api;
}

export function rejectionsLevelHelper(options: RejectionLevelHelperOptions) {
  const api = {
    get(code: number, type: RejectionType) {
      if (options.forceHardRejection) {
        return RejectionLevel.Hard;
      }

      const isAPISoftRejectable =
        options.consumerType === ConsumerType.Api &&
        (options.executionType === ExecutionType.RC1 || options.executionType === ExecutionType.RC3SoftRejections);

      // Allow soft rejections for Quote (web) consumer type to enable passthrough soft rejection flow
      // This allows regular web users to proceed to details/customize with pricing for specific rejection codes
      if (
        options.softRejectionsEnabled &&
        !options.inPolicyMod &&
        (options.consumerType === ConsumerType.Staff ||
          options.consumerType === ConsumerType.Quote ||
          isAPISoftRejectable) &&
        softRejectionApplicableTypes.includes(type) &&
        ((softRejectCodes as readonly number[]).includes(code) ||
          (type === RejectionType.Farm && farmSoftRejectCodes.includes(code)))
      ) {
        return RejectionLevel.Soft;
      }

      return RejectionLevel.Hard;
    }
  };

  return api;
}
